import { Button } from "@ucl/dse/button";
import { SingleSelectInput } from "@ucl/dse/selectInput";
import { TextInput, TextInputProps } from "@ucl/dse/textInput";
import { useTranslation } from "react-i18next";
import { useBoundInput } from "src/hooks/use-bound-input";
import { Text } from "@ucl/dse/text";
import { ChangeEvent } from "react";
import styles from "./address-select.module.scss";

interface AddressSelectProps {
    addressType: "currentAddress" | "previousAddress";
    postcodeLookupProps: TextInputProps;
    addressOptions: { value: string; label: string }[];
    onSelectedAddressChange: (value: { value: string; label: string }) => void;
    manualAddress: boolean;
    setManualAddress: (value: boolean) => void;
}

export const AddressSelect = ({
    addressType,
    postcodeLookupProps,
    addressOptions,
    onSelectedAddressChange,
    manualAddress,
    setManualAddress,
}: AddressSelectProps) => {
    const { t } = useTranslation();
    const addressSelectorProps = useBoundInput({ name: `${addressType}.addressSelector` });
    const hasSelectedAddress = !!addressSelectorProps.value;
    const selectedAddressLabel = addressOptions.find((opt) => opt.value === addressSelectorProps.value)?.label;

    const handleReset = () => {
        addressSelectorProps.onChange("");
        if (postcodeLookupProps.onChange) {
            const syntheticEvent = {
                target: { value: "" },
            } as ChangeEvent<HTMLInputElement>;
            postcodeLookupProps.onChange(syntheticEvent);
        }
    };

    const shouldShowAddressSelectorDropdown =
        !hasSelectedAddress &&
        !!postcodeLookupProps.value &&
        addressOptions &&
        addressOptions.length > 0 &&
        addressOptions[0].value !== "";

    return (
        <>
            {!hasSelectedAddress ? (
                <div className={styles.addressPickerGroup}>
                    <div className={styles.lookupWrapper}>
                        <TextInput
                            data-testid={"postcodeLookup"}
                            labelProp={{ label: t("addressDetails.enterYourPostcode") }}
                            placeholder=""
                            autoComplete="off"
                            {...postcodeLookupProps}
                        />
                        {shouldShowAddressSelectorDropdown && (
                            <SingleSelectInput
                                name={addressSelectorProps.name}
                                ref={addressSelectorProps.ref}
                                onBlur={addressSelectorProps.onBlur}
                                inputState={addressSelectorProps.inputState}
                                helperTextProp={addressSelectorProps.helperTextProp}
                                data-testid={"addressSelector"}
                                options={addressOptions}
                                selected={addressSelectorProps.value}
                                onOptionSelect={(option) => {
                                    addressSelectorProps.onChange(option.value);
                                    onSelectedAddressChange(option);
                                }}
                                placeholder={t("addressDetails.placeholders.addressSelect")}
                                className={styles.addressSelector}
                            />
                        )}
                    </div>
                    <Button
                        variant="tertiary"
                        data-testid={"manualAddressToggle"}
                        className={styles.tertiaryButton}
                        onClick={() => setManualAddress(!manualAddress)}
                    >
                        {!manualAddress && t("addressDetails.enterYourAddress")}
                    </Button>
                </div>
            ) : (
                <div>
                    <Text.P fontWeight="semi-bold" data-testid={"selectedAddressLabel"}>
                        {t("addressDetails.selectedAddress")}
                    </Text.P>
                    <Text.P data-testid={"selectedAddress"} className={styles.selectedAddress}>
                        {selectedAddressLabel}
                    </Text.P>
                    <div className={styles.actionButtons}>
                        <Button
                            variant="tertiary"
                            data-testid={"manualAddressToggle"}
                            className={styles.tertiaryButton}
                            onClick={() => setManualAddress(!manualAddress)}
                        >
                            {!manualAddress && t("addressDetails.enterYourAddress")}
                        </Button>
                        <Button
                            variant="tertiary"
                            data-testid={"searchAnotherAddress"}
                            className={styles.tertiaryButton}
                            onClick={handleReset}
                        >
                            {t("addressDetails.searchAnotherAddress")}
                        </Button>
                    </div>
                </div>
            )}
        </>
    );
};
